"use client";
import { useEffect, useState } from "react";
import { startOfWeek, endOfWeek, eachDayOfInterval, format } from "date-fns";
import { nl } from "date-fns/locale";
import { 
  fetchDayAvailability, 
  fetchWeekAvailability,
  openDay, 
  blockDay, 
  openWeek, 
  blockWeek, 
  moveSlot,
  setWeekFull as setWeekFullAPI,
  bookSlot,
  blockTimeSlot,
  unblockTimeSlot
} from "../../lib/scheduleApi";
import { Availability } from "../../types/schedule";

export function CalendarAdmin() {
  const [view, setView] = useState<"day" | "week" | "month">("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [availability, setAvailability] = useState<Availability[]>([]);
  const [studioId] = useState("studio-1"); // Default studio
  const [weekFull, setWeekFull] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTimeBlockModal, setShowTimeBlockModal] = useState(false);
  const [timeBlockForm, setTimeBlockForm] = useState({
    date: '',
    startTime: '',
    endTime: '',
    reason: ''
  });
  const [draggedItem, setDraggedItem] = useState<any>(null);
  const [dragOverDate, setDragOverDate] = useState<Date | null>(null);
  const [quickBlockMode, setQuickBlockMode] = useState<boolean>(false);

  const loadAvailability = async () => {
    setLoading(true);
    try {
      if (view === "day") {
        const data = await fetchDayAvailability(studioId, currentDate);
        setAvailability(data);
      } else if (view === "week") {
        const data = await fetchWeekAvailability(studioId, currentDate);
        setAvailability(data);
      } else if (view === "month") {
        // Load entire month data
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        
        // Generate slots for each day of the month
        const monthData = [];
        const currentDay = new Date(startOfMonth);
        
        while (currentDay <= endOfMonth) {
          const dayData = await fetchDayAvailability(studioId, new Date(currentDay));
          monthData.push(...dayData);
          currentDay.setDate(currentDay.getDate() + 1);
        }
        
        setAvailability(monthData);
      }
    } catch (error) {
      console.error("Error loading availability:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAvailability();
  }, [view, currentDate, studioId]);

  const handleOpenDay = async () => {
    try {
      await openDay(studioId, currentDate);
      await loadAvailability();
    } catch (error) {
      console.error("Error opening day:", error);
    }
  };

  const handleBlockDay = async () => {
    try {
      await blockDay(studioId, currentDate);
      await loadAvailability();
    } catch (error) {
      console.error("Error blocking day:", error);
    }
  };

  const handleOpenWeek = async () => {
    try {
      await openWeek(studioId, currentDate);
      await loadAvailability();
    } catch (error) {
      console.error("Error opening week:", error);
    }
  };

  const handleBlockWeek = async () => {
    try {
      await blockWeek(studioId, currentDate);
      await loadAvailability();
    } catch (error) {
      console.error("Error blocking week:", error);
    }
  };

  const handleWeekFullToggle = async () => {
    try {
      await setWeekFullAPI(studioId, currentDate, !weekFull);
      setWeekFull(!weekFull);
      await loadAvailability();
    } catch (error) {
      console.error("Error toggling week full:", error);
    }
  };

  const handleSlotDrop = async (slot: Availability) => {
    if (draggedItem?.type === 'quickBlockSlot') {
      if (slot.status === 'open') {
        const confirmBlock = window.confirm(
          `Wil je dit tijdvak blokkeren?\n\n${formatTime(slot.start)} - ${formatTime(slot.end)}`
        );
        
        if (confirmBlock) {
          try {
            await blockTimeSlot(studioId, slot.start.toDate(), slot.end.toDate(), "Quick block");
            await loadAvailability();
            alert("Tijdvak succesvol geblokkeerd!");
          } catch (error) {
            console.error("Error blocking slot:", error);
            alert("Kon tijdvak niet blokkeren: " + (error instanceof Error ? error.message : String(error)));
          }
        }
      } else {
        alert("Dit tijdvak is al geblokkeerd of geboekt!");
      }
      return;
    }
    
    // Fall back to normal slot click behavior
    await handleSlotClick(slot);
  };

  const handleSlotClick = async (slot: Availability) => {
    if (slot.status === "open") {
      try {
        await bookSlot(
          studioId,
          slot.start.toDate(),
          slot.end.toDate(),
          "current-user", // Replace with actual user ID
          [],
          "Podcast booking"
        );
        await loadAvailability();
      } catch (error) {
        console.error("Error booking slot:", error);
        alert("Kon slot niet boeken: " + (error instanceof Error ? error.message : String(error)));
      }
    } else if (slot.status === "blocked") {
      // Ask for confirmation to unblock
      const confirmUnblock = window.confirm(
        `Wil je dit tijdvak deblokkeren?\n\n${formatTime(slot.start)} - ${formatTime(slot.end)}\n${slot.reason ? `Reden: ${slot.reason}` : ''}`
      );
      
      if (confirmUnblock) {
        try {
          await unblockTimeSlot(studioId, slot.start.toDate(), slot.end.toDate());
          await loadAvailability();
          alert("Tijdvak succesvol gedeblokkeerd!");
        } catch (error) {
          console.error("Error unblocking slot:", error);
          alert("Kon tijdvak niet deblokkeren: " + (error instanceof Error ? error.message : String(error)));
        }
      }
    }
  };

  const handleTimeBlock = async () => {
    if (!timeBlockForm.date || !timeBlockForm.startTime || !timeBlockForm.endTime) {
      alert("Vul alle velden in");
      return;
    }

    try {
      const startDateTime = new Date(`${timeBlockForm.date}T${timeBlockForm.startTime}`);
      const endDateTime = new Date(`${timeBlockForm.date}T${timeBlockForm.endTime}`);
      
      // Block the specific time slot
      await blockTimeSlot(studioId, startDateTime, endDateTime, timeBlockForm.reason);
      
      // Reset form and close modal
      setTimeBlockForm({ date: '', startTime: '', endTime: '', reason: '' });
      setShowTimeBlockModal(false);
      await loadAvailability();
      
      alert("Tijdvak succesvol geblokkeerd!");
    } catch (error) {
      console.error("Error blocking time slot:", error);
      alert("Kon tijdvak niet blokkeren: " + (error instanceof Error ? error.message : String(error)));
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e: React.DragEvent, item: any) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    
    // Activate quick block mode if dragging quickBlockSlot
    if (item.type === 'quickBlockSlot') {
      setQuickBlockMode(true);
    }
  };

  const handleDragOver = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(date);
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDrop = async (e: React.DragEvent, targetDate: Date) => {
    e.preventDefault();
    setDragOverDate(null);

    if (!draggedItem) return;

    try {
      if (draggedItem.type === 'block') {
        // Block the target date
        await blockDay(studioId, targetDate);
      } else if (draggedItem.type === 'open') {
        // Open the target date
        await openDay(studioId, targetDate);
      } else if (draggedItem.type === 'blockWeek') {
        // Block the week containing the target date
        await blockWeek(studioId, targetDate);
      } else if (draggedItem.type === 'openWeek') {
        // Open the week containing the target date
        await openWeek(studioId, targetDate);
      } else if (draggedItem.type === 'blockTimeSlot') {
        // Open time slot blocking modal for the target date
        setTimeBlockForm({
          date: targetDate.toISOString().split('T')[0],
          startTime: '',
          endTime: '',
          reason: ''
        });
        setShowTimeBlockModal(true);
      } else if (draggedItem.type === 'timeSlot') {
        // Move time slot to new date
        const newStart = new Date(targetDate);
        const newEnd = new Date(targetDate);
        const [hours, minutes] = draggedItem.startTime.split(':').map(Number);
        newStart.setHours(hours, minutes, 0, 0);
        const [endHours, endMinutes] = draggedItem.endTime.split(':').map(Number);
        newEnd.setHours(endHours, endMinutes, 0, 0);

        await moveSlot(studioId, draggedItem.originalStart, draggedItem.originalEnd, newStart, newEnd);
      } else if (draggedItem.type === 'quickBlockSlot') {
        // Quick block - this will be handled by slot click events
        // We'll show a message that user should drag to specific time slots
        alert("🚫 Sleep naar een specifiek tijdvak om direct te blokkeren!");
        return;
      }
      
      await loadAvailability();
    } catch (error) {
      console.error("Error handling drop:", error);
      alert("Kon actie niet uitvoeren");
    }

    setDraggedItem(null);
    setQuickBlockMode(false);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setQuickBlockMode(false);
  };

  const getSlotColor = (status: string) => {
    switch (status) {
      case "open": return "bg-green-200 border-green-400 text-green-800";
      case "booked": return "bg-blue-200 border-blue-400 text-blue-800";
      case "blocked": return "bg-gray-200 border-gray-400 text-gray-600";
      default: return "bg-gray-100 border-gray-300 text-gray-500";
    }
  };

  const formatTime = (timestamp: any) => {
    if (timestamp && timestamp.toDate) {
      return timestamp.toDate().toLocaleTimeString("nl-NL", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Amsterdam"
      });
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleTimeString("nl-NL", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Amsterdam"
      });
    }
    return "00:00";
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("nl-NL", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "Europe/Amsterdam"
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header Controls */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        {/* View Toggle */}
        <div className="flex border rounded-lg">
          <button 
            className={`px-3 py-2 text-sm font-medium ${
              view === "day" ? "bg-purple-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
            onClick={() => setView("day")}
          >
            Dag
          </button>
          <button 
            className={`px-3 py-2 text-sm font-medium ${
              view === "week" ? "bg-purple-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
            onClick={() => setView("week")}
          >
            Week
          </button>
          <button 
            className={`px-3 py-2 text-sm font-medium ${
              view === "month" ? "bg-purple-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
            onClick={() => setView("month")}
          >
            Maand
          </button>
        </div>

        <div className="mx-3" />

        {/* Date Navigation */}
        <div className="flex items-center gap-2">
          <button 
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            onClick={() => {
              const newDate = new Date(currentDate);
              if (view === "day") newDate.setDate(newDate.getDate() - 1);
              else if (view === "week") newDate.setDate(newDate.getDate() - 7);
              else newDate.setMonth(newDate.getMonth() - 1);
              setCurrentDate(newDate);
            }}
          >
            ←
          </button>
          <span className="px-3 py-2 text-sm font-medium min-w-[200px] text-center">
            {formatDate(currentDate)}
          </span>
          <button 
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            onClick={() => {
              const newDate = new Date(currentDate);
              if (view === "day") newDate.setDate(newDate.getDate() + 1);
              else if (view === "week") newDate.setDate(newDate.getDate() + 7);
              else newDate.setMonth(newDate.getMonth() + 1);
              setCurrentDate(newDate);
            }}
          >
            →
          </button>
        </div>

        <div className="mx-3" />

        {/* Drag & Drop Tools */}
        <div className="flex gap-2 flex-wrap">
          <div
            draggable
            onDragStart={(e) => handleDragStart(e, { type: 'block', label: 'Block Dag' })}
            className="px-3 py-2 bg-red-100 text-red-700 rounded cursor-move hover:bg-red-200 text-sm font-medium border-2 border-dashed border-red-300"
            title="Sleep naar dag om te blokkeren"
          >
            🔴 Block Dag
          </div>
          <div
            draggable
            onDragStart={(e) => handleDragStart(e, { type: 'open', label: 'Open Dag' })}
            className="px-3 py-2 bg-green-100 text-green-700 rounded cursor-move hover:bg-green-200 text-sm font-medium border-2 border-dashed border-green-300"
            title="Sleep naar dag om te openen"
          >
            🟢 Open Dag
          </div>
          <div
            draggable
            onDragStart={(e) => handleDragStart(e, { type: 'blockWeek', label: 'Block Week' })}
            className="px-3 py-2 bg-red-100 text-red-700 rounded cursor-move hover:bg-red-200 text-sm font-medium border-2 border-dashed border-red-300"
            title="Sleep naar dag om hele week te blokkeren"
          >
            🔴 Block Week
          </div>
          <div
            draggable
            onDragStart={(e) => handleDragStart(e, { type: 'openWeek', label: 'Open Week' })}
            className="px-3 py-2 bg-green-100 text-green-700 rounded cursor-move hover:bg-green-200 text-sm font-medium border-2 border-dashed border-green-300"
            title="Sleep naar dag om hele week te openen"
          >
            🟢 Open Week
          </div>
          <div
            draggable
            onDragStart={(e) => handleDragStart(e, { type: 'blockTimeSlot', label: 'Block Tijdvak' })}
            className="px-3 py-2 bg-orange-100 text-orange-700 rounded cursor-move hover:bg-orange-200 text-sm font-medium border-2 border-dashed border-orange-300"
            title="Sleep naar dag om tijdvak te blokkeren (opent modal)"
          >
            ⏰ Block Tijdvak
          </div>
          <div
            draggable
            onDragStart={(e) => handleDragStart(e, { type: 'quickBlockSlot', label: 'Quick Block' })}
            className="px-3 py-2 bg-red-100 text-red-700 rounded cursor-move hover:bg-red-200 text-sm font-medium border-2 border-dashed border-red-300"
            title="Sleep naar tijdvak om direct te blokkeren"
          >
            🚫 Quick Block
          </div>
        </div>
        
        {/* Instructions */}
        <div className="text-xs text-gray-500 mt-2">
          💡 Sleep tools naar kalender dagen • Klik op tijdvakken om te boeken/deblokkeren • ⏰ Block Tijdvak opent modal • 🚫 Quick Block: sleep naar specifiek tijdvak om direct te blokkeren
        </div>

        <div className="ml-auto">
          <a 
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 text-sm font-medium"
            href="/vibescore"
          >
            VibeScore
          </a>
        </div>
      </div>

      {/* Week Full Banner */}
      {weekFull && (
        <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-yellow-800 font-medium">
              ⚠️ Week is gemarkeerd als vol - nieuwe boekingen niet mogelijk
            </span>
            <button 
              className="text-yellow-700 hover:text-yellow-900 text-sm underline"
              onClick={handleWeekFullToggle}
            >
              Opheffen
            </button>
          </div>
        </div>
      )}

      {/* Calendar Content */}
      <div className="flex-1 border rounded-lg p-6 bg-white">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Laden...</div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 mb-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-200 border border-green-400 rounded"></div>
                  <span>Open</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-200 border border-blue-400 rounded"></div>
                  <span>Geboekt</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-200 border border-gray-400 rounded"></div>
                  <span>Geblokkeerd</span>
                </div>
              </div>
            </div>

            {view === "day" && (
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Dag overzicht - {formatDate(currentDate)}</h3>
                {availability.length === 0 ? (
                  <div 
                    className={`text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg transition-all duration-200 ${
                      dragOverDate && dragOverDate.getTime() === currentDate.getTime() ? 'ring-2 ring-purple-400 bg-purple-100' : ''
                    } ${
                      quickBlockMode ? 'ring-1 ring-red-300 bg-red-25' : ''
                    }`}
                    onDragOver={(e) => handleDragOver(e, currentDate)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, currentDate)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="text-4xl mb-2">📅</div>
                    <p>Geen tijdvakken beschikbaar voor deze dag</p>
                    <p className="text-sm mt-1">Sleep een tool hierheen om actie uit te voeren</p>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    {availability.map((slot, index) => (
                      <div
                        key={index}
                        className={`p-3 border rounded-lg cursor-pointer hover:shadow-md transition-shadow ${getSlotColor(slot.status)} ${
                          quickBlockMode ? 'ring-2 ring-red-400 bg-red-50' : ''
                        }`}
                        onClick={() => handleSlotClick(slot)}
                        draggable
                        onDragStart={(e) => handleDragStart(e, { 
                          type: 'timeSlot', 
                          slot, 
                          startTime: formatTime(slot.start),
                          endTime: formatTime(slot.end),
                          originalStart: slot.start,
                          originalEnd: slot.end
                        })}
                        onDragOver={(e) => {
                          if (quickBlockMode) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'copy';
                          }
                        }}
                        onDrop={(e) => {
                          if (quickBlockMode) {
                            e.preventDefault();
                            handleSlotDrop(slot);
                          }
                        }}
                        title={`${formatTime(slot.start)} - ${formatTime(slot.end)} • ${slot.status === 'open' ? 'Klik om te boeken • Sleep om te verplaatsen' : slot.status === 'blocked' ? 'Klik om te deblokkeren • Sleep om te verplaatsen' : 'Geboekt • Sleep om te verplaatsen'}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium">
                            {formatTime(slot.start)} - {formatTime(slot.end)}
                          </span>
                          <span className="text-sm capitalize">
                            {slot.status === "open" ? "🟢 Klik om te boeken" : 
                             slot.status === "booked" ? "🔵 Geboekt" : 
                             slot.status === "blocked" ? "🔴 Klik om te deblokkeren" : slot.status}
                          </span>
                        </div>
                        {slot.status === "blocked" && (
                          <div className="text-xs text-gray-600 mt-1">
                            {slot.reason ? `Reden: ${slot.reason}` : "Klik om te deblokkeren"}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {view === "week" && (
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Week overzicht</h3>
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: 7 }, (_, i) => {
                    const day = new Date(currentDate);
                    day.setDate(currentDate.getDate() - currentDate.getDay() + 1 + i); // Maandag start
                    const daySlots = availability.filter(slot => 
                      slot.dateKey === day.toISOString().split('T')[0].replace(/-/g, '')
                    );
                    
                    // Check if day is completely blocked
                    const isDayBlocked = daySlots.length > 0 && daySlots.every(slot => slot.status === "blocked");
                    const hasOpenSlots = daySlots.some(slot => slot.status === "open");
                    const hasBookedSlots = daySlots.some(slot => slot.status === "booked");
                    
                    // Check if this day is being dragged over
                    const isDragOver = dragOverDate && dragOverDate.getTime() === day.getTime();
                    
                    return (
                  <div
                    key={i}
                    className={`border rounded-lg p-2 transition-all duration-200 ${
                      isDayBlocked ? 'bg-red-50 border-red-200' :
                      hasBookedSlots ? 'bg-blue-50 border-blue-200' :
                      hasOpenSlots ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    } ${
                      isDragOver ? 'ring-2 ring-purple-400 bg-purple-100' : ''
                    } ${
                      quickBlockMode ? 'ring-1 ring-red-300 bg-red-25' : ''
                    }`}
                    onDragOver={(e) => handleDragOver(e, day)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, day)}
                    onDragEnd={handleDragEnd}
                  >
                        <div className="text-sm font-medium mb-2 text-center">
                          {day.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric" })}
                        </div>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {daySlots.length === 0 ? (
                            <div className="text-xs text-gray-500 text-center italic">
                              Geen slots
                            </div>
                          ) : (
                            <>
                              {daySlots.map((slot, slotIndex) => (
                                <div
                                  key={slotIndex}
                                  className={`p-1 text-xs border rounded cursor-pointer hover:shadow-sm transition-shadow ${getSlotColor(slot.status)} ${
                                    quickBlockMode ? 'ring-1 ring-red-400 bg-red-50' : ''
                                  }`}
                                  onClick={() => handleSlotClick(slot)}
                                  onDragOver={(e) => {
                                    if (quickBlockMode) {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'copy';
                                    }
                                  }}
                                  onDrop={(e) => {
                                    if (quickBlockMode) {
                                      e.preventDefault();
                                      handleSlotDrop(slot);
                                    }
                                  }}
                                  title={`${formatTime(slot.start)} - ${formatTime(slot.end)} • ${slot.status === 'open' ? 'Klik om te boeken' : slot.status === 'blocked' ? 'Klik om te deblokkeren' : 'Geboekt'} ${quickBlockMode ? '• Sleep Quick Block hier om te blokkeren' : ''}`}
                                >
                                  <div className="flex justify-between items-center">
                                    <span>{formatTime(slot.start)}</span>
                                    <span className={`w-2 h-2 rounded-full ${
                                      slot.status === 'open' ? 'bg-green-400' :
                                      slot.status === 'blocked' ? 'bg-red-400' : 'bg-blue-400'
                                    }`}></span>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                        {isDayBlocked && (
                          <div className="text-xs text-red-600 text-center mt-1 font-medium">
                            🚫 Alle tijdvakken geblokkeerd
                          </div>
                        )}
                        {hasBookedSlots && !isDayBlocked && (
                          <div className="text-xs text-blue-600 text-center mt-1 font-medium">
                            🔵 Bezetting: {daySlots.filter(s => s.status === "booked").length}/{daySlots.length}
                          </div>
                        )}
                        {hasOpenSlots && !isDayBlocked && !hasBookedSlots && (
                          <div className="text-xs text-green-600 text-center mt-1 font-medium">
                            🟢 Alle tijdvakken beschikbaar
                          </div>
                        )}
                        {!hasOpenSlots && !hasBookedSlots && !isDayBlocked && daySlots.length === 0 && (
                          <div className="text-xs text-gray-500 text-center mt-1 font-medium">
                            ⚪ Geen slots gegenereerd
                          </div>
                        )}
                        {!hasOpenSlots && !hasBookedSlots && !isDayBlocked && daySlots.length > 0 && (
                          <div className="text-xs text-gray-500 text-center mt-1 font-medium">
                            ⚪ Alle slots geblokkeerd
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {view === "month" && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Maand overzicht - {format(currentDate, "MMMM yyyy", { locale: nl })}</h3>
                
                {/* Month Grid */}
                <div className="grid grid-cols-7 gap-2">
                  {/* Days of week headers */}
                  {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map(day => (
                    <div key={day} className="text-center text-sm font-medium text-gray-600 py-2 border-b">
                      {day}
                    </div>
                  ))}
                  
                  {/* Calendar days */}
                  {(() => {
                    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
                    const startOfWeek = new Date(startOfMonth);
                    startOfWeek.setDate(startOfMonth.getDate() - startOfMonth.getDay() + 1); // Start on Monday
                    const endOfWeek = new Date(endOfMonth);
                    endOfWeek.setDate(endOfMonth.getDate() + (7 - endOfMonth.getDay())); // End on Sunday
                    
                    const days = [];
                    const currentDay = new Date(startOfWeek);
                    
                    while (currentDay <= endOfWeek) {
                      const isCurrentMonth = currentDay.getMonth() === currentDate.getMonth();
                      const isToday = currentDay.toDateString() === new Date().toDateString();
                      
                      // Get slots for this day
                      const daySlots = availability.filter(slot => {
                        const slotDate = slot.start instanceof Date ? slot.start : slot.start.toDate();
                        return slotDate.toDateString() === currentDay.toDateString();
                      });
                      
                      const isDayBlocked = daySlots.length > 0 && daySlots.every(slot => slot.status === 'blocked');
                      const hasBookedSlots = daySlots.some(slot => slot.status === 'booked');
                      const hasOpenSlots = daySlots.some(slot => slot.status === 'open');
                      
                      days.push(
                        <div
                          key={currentDay.toISOString()}
                          className={`min-h-24 p-2 border rounded-lg transition-all duration-200 ${
                            isCurrentMonth ? 'bg-white' : 'bg-gray-50 text-gray-400'
                          } ${
                            isDayBlocked ? 'bg-red-50 border-red-200' :
                            hasBookedSlots ? 'bg-blue-50 border-blue-200' :
                            hasOpenSlots ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                          } ${
                            isToday ? 'ring-2 ring-purple-400' : ''
                          }`}
                          onDragOver={(e) => handleDragOver(e, currentDay)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, currentDay)}
                          onDragEnd={handleDragEnd}
                        >
                          <div className="text-sm font-medium mb-1">
                            {currentDay.getDate()}
                          </div>
                          <div className="max-h-20 overflow-y-auto space-y-1">
                            {daySlots.length === 0 ? (
                              <div className="text-xs text-gray-400 text-center italic">
                                Geen slots
                              </div>
                            ) : (
                              <>
                                {daySlots.map((slot, slotIndex) => (
                                  <div
                                    key={slotIndex}
                                    className={`p-1 text-xs border rounded cursor-pointer hover:shadow-sm transition-shadow ${getSlotColor(slot.status)} ${
                                      quickBlockMode ? 'ring-1 ring-red-400 bg-red-50' : ''
                                    }`}
                                    onClick={() => handleSlotClick(slot)}
                                    onDragOver={(e) => {
                                      if (quickBlockMode) {
                                        e.preventDefault();
                                        e.dataTransfer.dropEffect = 'copy';
                                      }
                                    }}
                                    onDrop={(e) => {
                                      if (quickBlockMode) {
                                        e.preventDefault();
                                        handleSlotDrop(slot);
                                      }
                                    }}
                                    title={`${formatTime(slot.start)} - ${formatTime(slot.end)} • ${slot.status === 'open' ? 'Klik om te boeken' : slot.status === 'blocked' ? 'Klik om te deblokkeren' : 'Geboekt'} ${quickBlockMode ? '• Sleep Quick Block hier om te blokkeren' : ''}`}
                                  >
                                    <div className="flex justify-between items-center">
                                      <span>{formatTime(slot.start)}</span>
                                      <span className={`w-1.5 h-1.5 rounded-full ${
                                        slot.status === 'open' ? 'bg-green-400' :
                                        slot.status === 'blocked' ? 'bg-red-400' : 'bg-blue-400'
                                      }`}></span>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                          {isDayBlocked && (
                            <div className="text-xs text-red-600 text-center mt-1 font-medium">
                              🚫
                            </div>
                          )}
                        </div>
                      );
                      
                      currentDay.setDate(currentDay.getDate() + 1);
                    }
                    
                    return days;
                  })()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Time Block Modal */}
      {showTimeBlockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">⏰ Specifiek Tijdvak Blokkeren</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Datum *</label>
                <input
                  type="date"
                  value={timeBlockForm.date}
                  onChange={(e) => setTimeBlockForm({...timeBlockForm, date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Starttijd *</label>
                  <input
                    type="time"
                    value={timeBlockForm.startTime}
                    onChange={(e) => setTimeBlockForm({...timeBlockForm, startTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eindtijd *</label>
                  <input
                    type="time"
                    value={timeBlockForm.endTime}
                    onChange={(e) => setTimeBlockForm({...timeBlockForm, endTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reden (optioneel)</label>
                <input
                  type="text"
                  value={timeBlockForm.reason}
                  onChange={(e) => setTimeBlockForm({...timeBlockForm, reason: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Bijv. Pauze, Onderhoud, Privé afspraak"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">💡 Voorbeeld:</p>
                  <p>Datum: Vandaag</p>
                  <p>Starttijd: 10:30</p>
                  <p>Eindtijd: 12:00</p>
                  <p>Reden: Pauze</p>
                  <p className="text-xs mt-1">→ Blokkeert 10:30-12:00 voor pauze</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowTimeBlockModal(false);
                  setTimeBlockForm({ date: '', startTime: '', endTime: '', reason: '' });
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleTimeBlock}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Tijdvak Blokkeren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
